import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Paper,
  Divider,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import LoginIcon from '@mui/icons-material/Login';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import MiscellaneousServicesOutlinedIcon from '@mui/icons-material/MiscellaneousServicesOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';

import axiosInstance from '../axiosConfig';

// ─── Main Component ──────────────────────────────────────────────────────────
const GuestServices = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#track' || location.state?.openTrack) {
      navigate('/guest-track-request', {
        replace: true,
        state: location.state?.requestNumber ? { requestNumber: location.state.requestNumber } : null,
      });
    }
  }, [location.hash, location.state, navigate]);

  // ── Services state ──
  const [categories, setCategories] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [serviceSearch, setServiceSearch] = useState('');
  const [servicesError, setServicesError] = useState('');

  // ── Load services ──
  useEffect(() => {
    axiosInstance
      .get('/Guest/AvailableServices')
      .then((data) => {
        setCategories(Array.isArray(data) ? data : []);
        setServicesError('');
      })
      .catch((err) => {
        const msg = err?.message || t('guest_services_load_error');
        setServicesError(msg);
        setCategories([]);
      })
      .finally(() => setLoadingServices(false));
  }, []);

  // ── Filter services by search ──
  const filteredCategories = useMemo(() => {
    if (!serviceSearch.trim()) return categories;
    const q = serviceSearch.toLowerCase();
    return categories
      .map((cat) => ({
        ...cat,
        publicServices: (cat.publicServices || []).filter((s) =>
          s.name?.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.publicServices.length > 0);
  }, [categories, serviceSearch]);

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'}>

      {/* ── Steps strip ──────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#fff', py: { xs: 2, md: 3 }, borderRadius: { xs: 2, md: 3 }, mb: 1 }}>
        <Container maxWidth="lg" sx={{ py: 0 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="center" flexWrap="wrap" gap={{ xs: 0, md: 1 }}>
            {[
              { num: 1, label: isRTL ? 'اختر الخدمة' : 'Choose a service', icon: <MiscellaneousServicesOutlinedIcon sx={{ fontSize: { xs: 28, md: 32 }, color: '#fff' }} /> },
              { num: 2, label: isRTL ? 'قدّم طلبك' : 'Submit your request', icon: <AssignmentTurnedInOutlinedIcon sx={{ fontSize: { xs: 28, md: 32 }, color: '#fff' }} /> },
              { num: 3, label: isRTL ? 'احتفظ برقم الطلب' : 'Save your request no.', icon: <ConfirmationNumberOutlinedIcon sx={{ fontSize: { xs: 28, md: 32 }, color: '#fff' }} /> },
              { num: 4, label: isRTL ? 'تابع حالة الطلب' : 'Track your request', icon: <TrackChangesIcon sx={{ fontSize: { xs: 28, md: 32 }, color: '#fff' }} /> },
            ].map((step, i, arr) => (
              <React.Fragment key={step.num}>
                <Box sx={{ textAlign: 'center', px: { xs: 1.5, sm: 2.5, md: 3.5 }, py: { xs: 2, md: 2.5 }, flex: '0 1 auto', minWidth: 0 }}>
                  <Box sx={{ position: 'relative', width: 'fit-content', mx: 'auto', mb: 1.5 }}>
                    <Box sx={{
                      width: { xs: 68, md: 80 }, height: { xs: 68, md: 80 }, borderRadius: '20px',
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.primary.light} 100%)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.38)}`,
                      transition: 'transform 0.22s ease, box-shadow 0.22s ease',
                      '&:hover': { transform: 'translateY(-4px) scale(1.06)', boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.52)}` },
                    }}>
                      {step.icon}
                    </Box>
                    <Box sx={{
                      position: 'absolute', top: -9, right: isRTL ? 'auto' : -9, left: isRTL ? -9 : 'auto',
                      width: 26, height: 26, borderRadius: '50%',
                      bgcolor: '#fff', border: `2.5px solid ${theme.palette.primary.main}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.28)}`,
                    }}>
                      <Typography sx={{ color: theme.palette.primary.main, fontWeight: 900, fontSize: '0.72rem', lineHeight: 1 }}>{step.num}</Typography>
                    </Box>
                  </Box>
                  <Typography fontWeight={700} color="text.primary" sx={{ lineHeight: 1.4, fontSize: { xs: '0.78rem', sm: '0.85rem', md: '0.92rem' }, maxWidth: { xs: 90, md: 110 }, mx: 'auto' }}>
                    {step.label}
                  </Typography>
                </Box>
                {i < arr.length - 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', pt: { xs: '42px', md: '50px' }, px: { xs: 0, md: 0.5 } }}>
                    <ArrowForwardIosIcon sx={{ fontSize: { xs: 14, md: 18 }, color: alpha(theme.palette.primary.main, 0.45), transform: isRTL ? 'rotate(180deg)' : 'none' }} />
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>

        {/* ── Services Section ─────────────────────────────────────────── */}
        <Box id="services" sx={{ scrollMarginTop: 80 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} mb={3}>
            <Box>
              <Typography variant="h5" fontWeight={700}>{t('guest_submitCard_title')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('guest_submitCard_desc')}</Typography>
            </Box>
            <Button
              variant="outlined" startIcon={<LoginIcon />} onClick={() => navigate('/login')}
              sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {t('login')}
            </Button>
          </Stack>

          {/* Search bar */}
          <TextField
            fullWidth
            placeholder={t('searchForService')}
            value={serviceSearch}
            onChange={(e) => setServiceSearch(e.target.value)}
            size="small"
            sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />

          {loadingServices ? (
            <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>
          ) : servicesError ? (
            <Alert severity="warning" sx={{ borderRadius: 3 }}>{servicesError}</Alert>
          ) : filteredCategories.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('noServicesAvailable')}</Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {filteredCategories.map((cat, ci) => (
                <Accordion
                  key={cat.id ?? ci}
                  defaultExpanded={ci === 0 && !serviceSearch}
                  disableGutters
                  sx={{ borderRadius: '12px !important', overflow: 'hidden', border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`, boxShadow: 'none', '&:before': { display: 'none' } }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary" />}
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04), minHeight: 52 }}
                  >
                    <Typography fontWeight={700} color="primary.main">{cat.name}</Typography>
                    <Chip label={(cat.publicServices || []).length} size="small" sx={{ ml: isRTL ? 0 : 1, mr: isRTL ? 1 : 0, height: 20, fontSize: '0.7rem' }} />
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <Grid container>
                      {(cat.publicServices || []).map((svc, si) => (
                        <Grid item xs={12} sm={6} md={4} key={svc.serviceId ?? si}>
                          <Box
                            sx={{
                              p: 2.5, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
                              borderBottom: `1px solid ${alpha('#000', 0.06)}`,
                              transition: 'background 0.18s, box-shadow 0.18s',
                              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06), boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}` },
                            }}
                            onClick={() =>
                              navigate('/guest-request', {
                                state: {
                                  serviceId: svc.serviceId,
                                  name: svc.name,
                                  applicationFee: svc.applicationFee,
                                  currency: svc.currency,
                                  groupName: cat.name,
                                  fragment: svc.fragment,
                                  serviceSelectType: svc.serviceSelectType,
                                  isServiceRequired: svc.isServiceRequired,
                                  requiredAttachments: svc.requiredAttachments || [],
                                  isWithCertificate: svc.isWithCertificate,
                                  filterService: svc.filterService,
                                },
                              })
                            }
                          >
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{
                                width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
                                background: alpha(theme.palette.primary.main, 0.1),
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                <SettingsIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                              </Box>
                              <Typography variant="body1" fontWeight={700} sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: { xs: '0.9rem', md: '1rem' } }}>
                                {svc.name}
                              </Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                              <Typography variant="body2" color="primary.main" fontWeight={800} noWrap sx={{ fontSize: '0.9rem' }}>
                                {svc.applicationFee} {svc.currency?.[0]?.code}
                              </Typography>
                              <ArrowForwardIosIcon sx={{ fontSize: 11, color: 'text.secondary', transform: isRTL ? 'rotate(180deg)' : 'none' }} />
                            </Stack>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </Box>

        {/* ── Track Screen Shortcut ─────────────────────────────────── */}
        <Divider sx={{ my: { xs: 4, md: 5 } }} />

        <Paper
          elevation={2}
          sx={{ borderRadius: 3, p: { xs: 2.5, md: 3.5 }, background: '#fff' }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: alpha(theme.palette.primary.main, 0.12),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <TrackChangesIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>{t('guest_track_title')}</Typography>
                <Typography variant="body2" color="text.secondary">{t('guest_track_screenSubtitle')}</Typography>
              </Box>
            </Stack>

            <Button
              variant="contained"
              onClick={() => navigate('/guest-track-request')}
              sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, px: 4 }}
            >
              {t('guest_track_screenCta')}
            </Button>
          </Stack>
        </Paper>

        </Container>
      </Box>
  );
};

export default GuestServices;
